import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { styled } from '@mui/material';
import { z } from 'zod';
import Dropzone, { UploadedFile } from 'components/Dropzone';
import TextField from 'components/FormElements/AppTextInput';
import FieldRow from 'components/FormElements/FieldRow';
import { validationMessages, validationRegexes } from 'further-ui/utils';
import { UploadType } from 'further-types/files';
import useFileUpload from 'hooks/ui/useFileUpload';
import Spacing from 'components/Spacing';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import { useNotification } from 'hooks/ui/useNotification';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { ADMIN_USER } from 'adminConstants/routes';
import api, { ApiResponses } from 'lib/trpcClient';
import AdminUserRoleSelector from 'components/AdminUserRoleSelector';
import { Row } from 'further-ui/components/Layout';
import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const StyledLink = styled(Button)(({ theme }) => ({
  padding: 0,
  fontSize: theme.fontSizes?.regular,
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

const firmUserSchema = z.object({
  firstName: z
    .string()
    .min(1, 'Please enter a first name.')
    .regex(validationRegexes.userName, validationMessages.nameValidationError),
  lastName: z
    .string()
    .min(1, 'Please enter a last name.')
    .regex(validationRegexes.userName, validationMessages.nameValidationError),
  email: z
    .string()
    .min(1, 'Please enter an email.')
    .regex(validationRegexes.email, 'Invalid email address.'),
  roleId: z.string().min(1, 'Please select a role.'),
  profilePic: z.string().nullable().optional(),
  previewProfilePic: z.string().nullable().optional(),
});

type FormData = z.infer<typeof firmUserSchema>;

type FirmUser = ApiResponses['adminUser']['getUserInCurrentFirm'];

type Props = {
  id?: string;
  firmUser?: FirmUser;
};

const FirmUserFormFields: FC<Props> = ({ id, firmUser }) => {
  const { error } = useNotification();
  const { uploadFile } = useFileUpload();
  const { profile } = useCurrentUser();
  const isSelfEditing = id === profile.data?._id;
  const [canManageUserSecurity] = useHasPermissions([
    RolePermission.ManageUserSecurity,
  ]);

  const createFirmUser = api.adminUser.createUserInCurrentFirm.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'User created successfully.',
      successRedirect: ADMIN_USER,
    }),
  );
  const editFirmUser = api.adminUser.updateUserInCurrentFirm.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'User updated successfully.',
    }),
  );
  const requestPasswordReset =
    api.adminUser.requestPasswordResetForUser.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Password reset email sent.',
      }),
    );

  const form = useForm<FormData>({
    shouldFocusError: true,
    resolver: zodResolver(firmUserSchema),
    values: {
      firstName: firmUser?.firstName ?? '',
      lastName: firmUser?.lastName ?? '',
      email: firmUser?.email ?? '',
      roleId: firmUser?.roles?.[0]?.auth0RoleId ?? '',
      previewProfilePic: firmUser?.profilePicUrl ?? null,
      profilePic: firmUser?.profilePic ?? '',
    },
    criteriaMode: 'all',
  });

  const onSubmit = async (formValues: FormData) => {
    if (id) {
      editFirmUser.mutate({
        adminUserId: id,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        newAuth0RoleId: formValues.roleId,
        profilePic: formValues.profilePic ?? undefined,
      });
    } else {
      createFirmUser.mutate({
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        auth0RoleId: formValues.roleId,
        profilePic: formValues.profilePic ?? undefined,
      });
    }
  };

  const handleProfileImage = async (file: Array<UploadedFile>) => {
    if (!file?.[0]?.path) return;

    const { data, error: uploadError } = await uploadFile(
      file[0],
      UploadType.UserImages,
    );
    if (data) {
      form.setValue('profilePic', data.filePath);
      form.setValue('previewProfilePic', file[0]?.preview);
    } else {
      error(uploadError?.message);
    }
  };

  const clearProfileImage = () => {
    form.setValue('profilePic', null);
    form.setValue('previewProfilePic', null);
  };

  const formIsSubmitting = createFirmUser.isPending || editFirmUser.isPending;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Spacing>
        <FieldRow title="First name" centerTitle>
          <Controller
            name="firstName"
            control={form.control}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <TextField
                {...rest}
                inputRef={ref}
                fullWidth
                variant="outlined"
                error={!!error}
                helperText={error?.message}
                disabled={formIsSubmitting}
              />
            )}
          />
        </FieldRow>
        <FieldRow title="Last name" centerTitle>
          <Controller
            name="lastName"
            control={form.control}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <TextField
                {...rest}
                inputRef={ref}
                fullWidth
                variant="outlined"
                error={!!error}
                helperText={error?.message}
                disabled={formIsSubmitting}
              />
            )}
          />
        </FieldRow>
        <FieldRow title="Email" centerTitle>
          <Controller
            name="email"
            control={form.control}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <TextField
                {...rest}
                inputRef={ref}
                fullWidth
                variant="outlined"
                error={!!error}
                helperText={error?.message}
                disabled={!!id || formIsSubmitting}
              />
            )}
          />
        </FieldRow>
        <FieldRow title="Role" centerTitle>
          <Controller
            name="roleId"
            control={form.control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <AdminUserRoleSelector
                value={value}
                onChange={onChange}
                disabled={formIsSubmitting || isSelfEditing}
                error={!!error}
                helperText={error?.message}
                placeholder="Select a role"
              />
            )}
          />
        </FieldRow>
        {id && canManageUserSecurity && (
          <FieldRow title="Password" centerTitle>
            <Spacing size="sm">
              <TextField
                variant="outlined"
                value="********"
                disabled
                fullWidth
              />
              <ButtonGroup align="left">
                <StyledLink
                  variant="text"
                  onClick={() => requestPasswordReset.mutate({ id })}
                  loading={requestPasswordReset.isPending}
                >
                  Send password reset email
                </StyledLink>
              </ButtonGroup>
            </Spacing>
          </FieldRow>
        )}
        <FieldRow title="Profile image" centerTitle>
          <Controller
            name="previewProfilePic"
            control={form.control}
            render={({ field: { value, name } }) => (
              <Dropzone
                name={name}
                files={value ? [{ preview: value, fileName: '' }] : []}
                onDropFile={(files) => handleProfileImage(files)}
                onRemove={clearProfileImage}
                defaultAcceptGroup="images"
              />
            )}
          />
        </FieldRow>
        <Row spacing="sm" centered justify="flex-end">
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={formIsSubmitting}
          >
            {id ? 'Update' : 'Add & invite user'}
          </Button>
        </Row>
      </Spacing>
    </form>
  );
};

export default FirmUserFormFields;
